import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
  Autocomplete,
  Typography,
  Divider,
  Card,
  CardContent,
  Tooltip,
  Box,
  Chip,
  Button,
} from "@mui/material";
import {
  getAllMilestonesWithStatuses,
  CreateMilestone,
  saveMileStoneEdit,
  CreateMilestoneStatus,
  saveMileStoneStatusEdit,
  getMilestoneDataType,
  getMilestoneWithListType,
  insertBulkMilestone,
} from "../../services/userServices";
import classes from "./MileStones.module.css";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EnhancedDataGrid from "components/EnhancedDataGrid/EnhancedDataGrid";
import BackdropComponent from "components/Spinner/Spinner";
import SnackBar from "components/SnackBar/SnackBar";
import applicationUsageLog from "../../utils/applicationUsagesLog";
import { InfoOutlined } from "@mui/icons-material";
import { updatesMilestonesReorder } from "../../services/userServices";
import ApplyToAllDialog from "../../components/MileStones/ApplyToAllDialog";

// ─── Constants ────────────────────────────────────────────────────────────────

const MILESTONE_MAX_LENGTH = 50;

const FIELD_LABEL_SX = {
  fontSize: "12px",
  fontFamily: "Urbanist, sans-serif",
  color: "#6B7B8A",
  fontWeight: "600",
  mb: "5px",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

const INPUT_SX = { width: "240px", "& .MuiInputBase-root": { width: "240px" } };

const ADD_BUTTON_SX = {
  backgroundColor: "#2AA154",
  color: "#fff",
  borderRadius: "8px",
  width: 38,
  height: 38,
  border: 0,
  "&:hover": { backgroundColor: "#238a45" },
  "&:active": { transform: "scale(0.97)" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Module-level so the reference stays stable across renders (keeps EnhancedDataGrid's memoization working).
const milestoneConfirmChangeFields = ["dataTypeId"];

// Same reason as above.
const milestoneFilterableFields = [
  "milestone",
  "milestoneTypeID",
  "dataTypeId",
  "isActive",
  "lastModifiedBy",
];

const createData = (data) =>
  data.map((item, index) => ({
    ...item,
    id: item.milestoneID ?? index + 1,
    displayOrder: item.displayOrder ?? index + 1,
    edit: false,
  }));

const formatLastModifiedDate = (dateValue) => {
  if (!dateValue) return dateValue;
  const parsed = dayjs(dateValue);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY hh:mm A") : dateValue;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Labelled input wrapper — keeps label + field vertically paired */
const FieldGroup = ({ label, children }) => (
  <Box>
    <Typography sx={FIELD_LABEL_SX}>{label}</Typography>
    {children}
  </Box>
);

/** Consistent section panel used inside both accordions */
const AddPanel = ({ children }) => (
  <Box
    sx={{
      backgroundColor: "#F8FAFB",
      border: "1px solid #E8ECF0",
      borderRadius: "8px",
      px: 2.5,
      py: 2,
      mb: 2.5,
    }}
  >
    {children}
  </Box>
);

// ─── Component ────────────────────────────────────────────────────────────────

export default function CreateMilestones() {
  const [dataTypeList, setDataTypeList] = useState([]);
  const [milestoneWithListType, setMilestoneWithListType] = useState([]);
  const [milestonesList, setMilestonesList] = useState([]);
  const [milestoneStatuses, setMilestoneStatuses] = useState([]);
  // Off by default -- sorting is primary; drag-to-reorder is opt-in via the toggle below.
  const [reorderModeEnabled, setReorderModeEnabled] = useState(false);
  // Mirrors the grid's filter state so the Reorder toggle can be disabled while filtering.
  const [filtersActive, setFiltersActive] = useState(false);
  const [AllMilestoneStatuses, setAllMilestoneStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [applyToAllDialog, setApplyToAllDialog] = useState({
    open: false,
    milestoneName: "",
    milestoneId: null,
  });

  const defaultMilestoneStatus = { MilestoneID: 0, Milestone: "" };
  const defaultDataType = { dataTypeId: 0, dataType: "" };

  const [selectedMilestoneStatus, setSelectedMilestoneStatus] = useState(
    defaultMilestoneStatus,
  );
  const [selectedDataList, setSelectedDataList] = useState(defaultDataType);

  const milestoneNameRef = useRef("");
  const milestoneStatusNameRef = useRef("");

  const snackReset = () => setSnack({ open: false, message: "", severity: "" });

  // ─── Data Fetching ──────────────────────────────────────────────────────────

  async function fetchData() {
    try {
      setLoading(true);
      const response = await getAllMilestonesWithStatuses(0);

      if (response?.status === true) {
        const [dataTypeResponse, milestoneWithListResponse] = await Promise.all(
          [getMilestoneDataType(), getMilestoneWithListType()],
        );

        if (dataTypeResponse?.status === true)
          setDataTypeList(createData(dataTypeResponse?.data));

        if (milestoneWithListResponse?.status === true)
          setMilestoneWithListType(createData(milestoneWithListResponse?.data));

        setMilestonesList(
          createData(
            (response?.data?.milestones || []).map((item) => ({
              ...item,
              lastModifiedDate: formatLastModifiedDate(item.lastModifiedDate),
            }))
          )
        );
        setAllMilestoneStatuses(createData(response?.data?.milestoneStatuses));
        filterMilestoneStatus(defaultMilestoneStatus?.MilestoneID);
        window.localStorage.removeItem("CreateMilestone");
      } else {
        setSnack({
          open: true,
          message: "Some internal error occurred.",
          severity: "error",
        });
      }
    } catch {
      setSnack({
        open: true,
        message: "Some internal error occurred.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    localStorage.setItem("CreateMilestone", window.location.pathname);

    const initialize = async () => {
      try {
        await fetchData();
        if (!cancelled) {
          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            "ASTT",
            "Create Milestone",
          );
        }
      } catch (error) {
        if (!cancelled) console.error("Failed to initialize:", error);
      }
    };

    initialize();
    return () => {
      cancelled = true;
    };
  }, []);

  // ─── Milestone Handlers ─────────────────────────────────────────────────────

  const handleAddMilestone = async () => {
    const milestoneName = milestoneNameRef.current.value.trim();

    if (!milestoneName) {
      setSnack({
        open: true,
        severity: "warning",
        message: "Please enter a milestone name.",
      });
      return;
    }
    if (!selectedDataList?.dataTypeId) {
      setSnack({
        open: true,
        severity: "warning",
        message: "Please select a data type for milestone.",
      });
      return;
    }

    const resetForm = () => {
      milestoneNameRef.current.value = "";
      setSelectedDataList(defaultDataType);
    };

    setLoading(true);
    try {
      const payload = {
        milestone: milestoneName,
        dataTypeId: selectedDataList.dataTypeId,
        createdBy: localStorage.getItem("User") || "",
      };
      const response = await CreateMilestone(payload);

      if (response?.statusCode === 200) {
        setSnack({
          open: true,
          severity: "success",
          message: "Milestone added successfully.",
        });
        fetchData();
      } else if (response?.statusCode === 409) {
        setSnack({
          open: true,
          severity: "warning",
          message: response?.message ?? "Milestone already exists.",
        });
      } else {
        setSnack({
          open: true,
          severity: "error",
          message: "Some internal error occurred.",
        });
      }
    } catch {
      setSnack({
        open: true,
        severity: "error",
        message: "Some internal error occurred.",
      });
    } finally {
      resetForm();
      setLoading(false);
    }
  };

  const handleSaveMilestoneEdit = async (rowId, data) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        modifiedBy: localStorage.getItem("User") || "",
      };
      // Compare against the pre-save list so the success message can call out a Data Type change.
      const originalMilestone = milestonesList.find(
        (item) => item?.id === rowId,
      );
      const isDataTypeChange =
        originalMilestone &&
        payload?.dataTypeId != null &&
        originalMilestone?.dataTypeId !== payload.dataTypeId;

      const response = await saveMileStoneEdit(payload);

      if (response?.statusCode === 200) {
        setSnack({
          open: true,
          severity: "success",
          message: isDataTypeChange
            ? "Milestone Data Type updated successfully."
            : "Milestone saved successfully.",
        });
        fetchData();
      } else if (response?.statusCode === 409) {
        setSnack({
          open: true,
          severity: "warning",
          message: response?.message ?? "Milestone already exists.",
        });
      } else {
        setSnack({
          open: true,
          severity: "error",
          message: response?.message ?? "Some internal error occurred.",
        });
      }
    } catch {
      setSnack({
        open: true,
        severity: "error",
        message: "Some internal error occurred.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── Milestone Status Handlers ──────────────────────────────────────────────

  const handleAddMilestoneStatus = async () => {
    const milestoneStatus = milestoneStatusNameRef.current.value.trim();

    if (!milestoneStatus) {
      setSnack({
        open: true,
        severity: "warning",
        message: "Please enter a milestone status name.",
      });
      return;
    }
    if (!selectedMilestoneStatus?.MilestoneId) {
      setSnack({
        open: true,
        severity: "warning",
        message: "Please select a milestone.",
      });
      return;
    }

    const resetForm = () => {
      milestoneStatusNameRef.current.value = "";
      setSelectedMilestoneStatus(defaultMilestoneStatus);
    };

    setLoading(true);
    try {
      const payload = {
        milestoneID: selectedMilestoneStatus.MilestoneId,
        status: milestoneStatus,
      };
      const response = await CreateMilestoneStatus(payload);

      if (response?.statusCode === 200) {
        setSnack({
          open: true,
          severity: "success",
          message: "Milestone status added successfully.",
        });
        fetchData();
      } else if (response?.statusCode === 409) {
        setSnack({
          open: true,
          severity: "warning",
          message: response?.message ?? "Milestone status already exists.",
        });
      } else {
        setSnack({
          open: true,
          severity: "error",
          message: "Some internal error occurred.",
        });
      }
    } catch {
      setSnack({
        open: true,
        severity: "error",
        message: "Some internal error occurred.",
      });
    } finally {
      resetForm();
      setLoading(false);
    }
  };

  const handleSaveMilestoneStatusEdit = async (rowId, data) => {
    setLoading(true);
    try {
      const response = await saveMileStoneStatusEdit(data);

      if (response?.statusCode === 200) {
        setSnack({
          open: true,
          severity: "success",
          message: "Milestone status saved successfully.",
        });
        fetchData();
        setSelectedMilestoneStatus(defaultMilestoneStatus);
        milestoneStatusNameRef.current.value = "";
      } else if (response?.statusCode === 409) {
        setSnack({
          open: true,
          severity: "warning",
          message: response?.message ?? "Milestone status already exists.",
        });
      } else {
        setSnack({
          open: true,
          severity: "error",
          message: "Some internal error occurred.",
        });
      }
    } catch {
      setSnack({
        open: true,
        severity: "error",
        message: "Some internal error occurred.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── Misc Handlers ──────────────────────────────────────────────────────────

  const filterMilestoneStatus = (filterValue) => {
    const dataSource = AllMilestoneStatuses.filter(
      (ms) => ms.milestoneID === filterValue,
    ).map((item, index) => ({
      ...item,
      id: item.milestoneStatusId ?? index + 1,
      edit: false,
    }));
    setMilestoneStatuses(dataSource);
  };

  const handleMilestoneStatusChange = (_, value) => {
    if (value) {
      setSelectedMilestoneStatus(value);
      filterMilestoneStatus(value?.MilestoneId);
    } else {
      setSelectedMilestoneStatus(defaultMilestoneStatus);
      filterMilestoneStatus(defaultMilestoneStatus?.MilestoneID);
    }
  };

  const handleDataTypeStatusChange = (_, value) => {
    setSelectedDataList(value ?? defaultDataType);
  };

  // ─── Apply To All Handler ───────────────────────────────────────────────────

  const handleApplyToAll = (row) => {
    setApplyToAllDialog({
      open: true,
      milestoneName: row?.milestone ?? "",
      milestoneId: row?.milestoneID ?? row?.id,
    });
  };

  const handleApplyToAllClose = () => {
    setApplyToAllDialog({ open: false, milestoneName: "", milestoneId: null });
  };

  const handleApplyToAllProceed = async ({
    milestoneId,
    applyRosters,
    applyFrameworks,
  }) => {
    setLoading(true);
    try {
      const payload = {
        milestoneId,
        isAllClassRoster: applyRosters,
        isAllFramework: applyFrameworks,
        createdBy: localStorage.getItem("User"),
      };
      const response = await insertBulkMilestone(payload);
      if (response?.statusCode === 200) {
        setSnack({
          open: true,
          severity: "success",
          message: "Milestone applied successfully.",
        });
      } else {
        setSnack({
          open: true,
          severity: "error",
          message: "Some internal error occurred.",
        });
      }
    } catch {
      setSnack({
        open: true,
        severity: "error",
        message: "Some internal error occurred.",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveReorderedStatuses = async (updatedList) => {
    const reorderPayload = updatedList.map((item) => ({
      milestoneId: item.id,
      displayOrder: item.displayOrder,
    }));
    setLoading(true);
    const response = await updatesMilestonesReorder(reorderPayload);
    setSnack({
      open: true,
      message:
        response?.message === "Success"
          ? "Reorder saved."
          : "Failed to reorder.",
      severity: response?.message === "Success" ? "success" : "error",
    });
    setLoading(false);
  };

  // ─── Grid Config ─────────────────────────────────────────────────────────────

  // Only these four Data Types can be exchanged with each other; List/MultiSelect are excluded.
  const allowedDataTypes = ["Text", "Number", "Memo", "Date"];
  const allowedEditableDataTypes = useMemo(
    () => dataTypeList.filter((item) => allowedDataTypes.includes(item?.dataType)),
    [dataTypeList], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const isMilestoneDataTypeEditable = useCallback(
    (row) => allowedDataTypes.includes(row?.dataType),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const getMilestoneDataTypeConfirmMessage = useCallback(
    (row, originalRow) => {
      const dataTypeName = (id) =>
        dataTypeList.find((item) => item?.dataTypeId === id)?.dataType ??
        "Unknown";
      const fromName = dataTypeName(originalRow?.dataTypeId);
      const toName = dataTypeName(row?.dataTypeId);
      return `Change "${row?.milestone}"'s Data Type from ${fromName} to ${toName}? Values already entered under ${fromName} will be preserved but may not display as expected under ${toName}.`;
    },
    [dataTypeList],
  );

  // Memoized so the reference stays stable and EnhancedDataGrid doesn't reset edit state on every render.
  const milestoneHeadCells = useMemo(
    () => [
    {
      fieldName: "milestoneID",
      // Shortened from "Apply to All" to avoid wrapping in this narrow column.
      label: "Apply All",
      control: "applyToAll",
      editable: false,
    },
    {
      fieldName: "displayOrder",
      label: "Display Order",
      control: "label",
      editable: false,
      align: "right",
    },
    {
      fieldName: "milestone",
      label: "Milestone Name",
      control: "label",
      editable: true,
      align: "left",
    },
    {
      fieldName: "milestoneTypeID",
      label: "Milestone Type",
      control: "autoComplete",
      editable: true,
      align: "left",
      dataSource: [
        { milestoneTypeID: 1, milestoneType: "Universal" },
        { milestoneTypeID: 2, milestoneType: "OG" },
        { milestoneTypeID: 3, milestoneType: "LD" },
      ],
      displayFieldName: "milestoneType",
    },
    {
      fieldName: "dataTypeId",
      label: "Data Type",
      control: "autoComplete",
      editable: true,
      align: "left",
      // Full list, so labels resolve even for List/MultiSelect rows (shown read-only, see below).
      dataSource: dataTypeList,
      // Restricted picker options -- List/MultiSelect aren't selectable targets.
      editableOptions: allowedEditableDataTypes,
      displayFieldName: "dataType",
      // Editable only when the row's current type is Text/Number/Memo/Date.
      isEditableForRow: isMilestoneDataTypeEditable,
    },
    {
      fieldName: "isActive",
      label: "Active",
      control: "active",
      editable: true,
    },
    {
      fieldName: "lastModifiedBy",
      label: "Last Modified By",
      control: "label",
      editable: false,
      align: "left",
    },
    {
      fieldName: "lastModifiedDate",
      label: "Last Modified Date",
      control: "label",
      editable: false,
      align: "left",
    },
    ],
    [dataTypeList, allowedEditableDataTypes, isMilestoneDataTypeEditable],
  );

  const statusHeadCells = [
    { fieldName: "id", label: "#", control: "label", editable: false },
    { fieldName: "status", label: "Status", control: "label", editable: true },
    {
      fieldName: "isActive",
      label: "Active",
      control: "active",
      editable: true,
    },
  ];

  // ─── Derived state ───────────────────────────────────────────────────────────

  const milestoneCount = milestonesList.length;
  const milestoneStatusCount = milestoneStatuses.length;
  const hasMilestoneSelected = Boolean(selectedMilestoneStatus?.Milestone);

  const noStatusMessage = hasMilestoneSelected
    ? "No statuses found. Use the form above to add one."
    : "Select a milestone above to view and manage its statuses.";

  // ─── Shared accordion sx ─────────────────────────────────────────────────────

  const accordionSx = {
    border: "1px solid #E4E8ED",
    borderRadius: "10px !important",
    boxShadow: "none",
    "&:before": { display: "none" },
    overflow: "hidden",
  };

  const accordionSummarySx = (accentColor) => ({
    backgroundColor: "#F8FAFB",
    borderBottom: "1px solid #E4E8ED",
    minHeight: "54px",
    borderLeft: `4px solid ${accentColor}`,
    borderRadius: 0,
    "& .MuiAccordionSummary-content": { my: "13px", alignItems: "center" },
  });

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={classes.mainContainer}>
      {loading && <BackdropComponent />}

      <Card
        sx={{
          height: "calc(100vh - 100px)",
          overflowY: "auto",
          boxShadow: "none",
          border: "1px solid #E4E8ED",
          borderRadius: "10px",
        }}
      >
        <CardContent sx={{ px: 3, pt: 2.5, pb: 3 }}>
          {/* ── Page Header ── */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1.5,
                mb: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: "26px",
                  fontFamily: "Urbanist, sans-serif",
                  color: "#3F5765",
                  fontWeight: "bold",
                  lineHeight: 1.2,
                }}
              >
                Milestones
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontFamily: "Urbanist, sans-serif",
                  color: "#8A9BB0",
                }}
              >
                Create and edit milestones and their statuses
              </Typography>
            </Box>
            <Divider sx={{ mt: 1.5 }} />
          </Box>

          <Grid container spacing={2}>
            {/* ══════════════════════════════════════════
                SECTION 1 — Milestones
            ══════════════════════════════════════════ */}
            <Grid item xs={12}>
              <Accordion defaultExpanded sx={accordionSx}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#3F5765" }} />}
                  sx={accordionSummarySx("#3299FF")}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flex: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                        fontWeight: "bold",
                        color: "#3F5765",
                        fontSize: 18,
                      }}
                    >
                      Milestones
                    </Typography>
                    <Chip
                      label={milestoneCount}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "11px",
                        fontWeight: "bold",
                        backgroundColor: "#E8F1FF",
                        color: "#3299FF",
                        borderRadius: "6px",
                      }}
                    />
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 2.5, pb: 3, px: 2.5 }}>
                  {/* Add Milestone — input row */}
                  <AddPanel>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontFamily: "Urbanist, sans-serif",
                        color: "#3F5765",
                        fontWeight: "bold",
                        mb: 1.5,
                      }}
                    >
                      Add New Milestone
                    </Typography>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item>
                        <FieldGroup label="Milestone Name">
                          <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            placeholder="e.g. Training Completion"
                            sx={INPUT_SX}
                            inputRef={milestoneNameRef}
                            inputProps={{
                              className: classes.textFields,
                              maxLength: MILESTONE_MAX_LENGTH,
                            }}
                            InputProps={{ className: classes.inputText }}
                            onChange={(e) => {
                              milestoneNameRef.current.value =
                                e.target.value.replace(/[:\-]/g, " ");
                            }}
                          />
                        </FieldGroup>
                      </Grid>

                      <Grid item>
                        <FieldGroup label="Data Type">
                          <Autocomplete
                            sx={INPUT_SX}
                            options={dataTypeList}
                            value={selectedDataList}
                            getOptionLabel={(item) => item?.dataType}
                            onChange={handleDataTypeStatusChange}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select data type"
                                InputProps={{
                                  ...params.InputProps,
                                  className: classes.inputText,
                                }}
                              />
                            )}
                          />
                        </FieldGroup>
                      </Grid>

                      <Grid item>
                        <Tooltip title="Add milestone" placement="top">
                          <IconButton
                            onClick={handleAddMilestone}
                            aria-label="Add Milestone"
                            sx={ADD_BUTTON_SX}
                          >
                            <AddCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </AddPanel>

                  {/* Reorder mode toggle -- off by default, switches to drag-to-reorder on demand. */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mb: 1.5,
                    }}
                  >
                    <Tooltip
                      title={
                        !reorderModeEnabled && filtersActive
                          ? "Clear active filters to enable reordering"
                          : ""
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          // Only gates entering Reorder Mode, never exiting it.
                          disabled={!reorderModeEnabled && filtersActive}
                          onClick={() =>
                            setReorderModeEnabled((prev) => !prev)
                          }
                          startIcon={
                            reorderModeEnabled ? (
                              <CheckCircleOutlineIcon fontSize="small" />
                            ) : (
                              <DragIndicatorIcon fontSize="small" />
                            )
                          }
                          sx={{
                            textTransform: "none",
                            fontFamily: "Urbanist",
                            fontWeight: 600,
                        borderRadius: "6px",
                        color: reorderModeEnabled ? "#238a45" : "#3299FF",
                        "&:hover": {
                          backgroundColor: reorderModeEnabled
                            ? "#E7F6EC"
                            : "#EBF5FF",
                        },
                      }}
                    >
                      {reorderModeEnabled
                        ? "Done Reordering"
                        : "Reorder Rows"}
                    </Button>
                      </span>
                    </Tooltip>
                  </Box>

                  {/* Grid */}
                  <EnhancedDataGrid
                    key="Milestones"
                    headCells={milestoneHeadCells}
                    dataSource={milestonesList}
                    handleSave={handleSaveMilestoneEdit}
                    handleReorder={saveReorderedStatuses}
                    isReadOnly={false}
                    setVisibleRows={setMilestonesList}
                    dragEnabled={reorderModeEnabled}
                    onApplyToAll={handleApplyToAll}
                    filterableFields={milestoneFilterableFields}
                    onFilterActiveChange={setFiltersActive}
                    confirmChangeFields={milestoneConfirmChangeFields}
                    getConfirmMessage={getMilestoneDataTypeConfirmMessage}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* ══════════════════════════════════════════
                SECTION 2 — Milestone Statuses
            ══════════════════════════════════════════ */}
            <Grid item xs={12}>
              <Accordion sx={accordionSx}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#3F5765" }} />}
                  sx={accordionSummarySx("#3299FF")}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flex: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                        fontWeight: "bold",
                        color: "#3F5765",
                        fontSize: 18,
                      }}
                    >
                      Milestone Status
                    </Typography>
                    {hasMilestoneSelected && (
                      <Chip
                        label={milestoneStatusCount}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "11px",
                          fontWeight: "bold",
                          backgroundColor: "#E8F1FF",
                          color: "#3299FF",
                          borderRadius: "6px",
                        }}
                      />
                    )}
                    <Tooltip
                      title="Add statuses for milestones with data types: List & MultiSelect."
                      placement="right"
                    >
                      <InfoOutlined
                        sx={{
                          fontSize: 17,
                          color: "#B0BEC5",
                          ml: 0.5,
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 2.5, pb: 3, px: 2.5 }}>
                  {/* Add Milestone Status — input row */}
                  <AddPanel>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontFamily: "Urbanist, sans-serif",
                        color: "#3F5765",
                        fontWeight: "bold",
                        mb: 1.5,
                      }}
                    >
                      Add New Status
                    </Typography>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item>
                        <FieldGroup label="Milestone">
                          <Autocomplete
                            sx={INPUT_SX}
                            options={milestoneWithListType}
                            value={selectedMilestoneStatus}
                            getOptionLabel={(item) => item?.Milestone}
                            onChange={handleMilestoneStatusChange}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select milestone"
                                InputProps={{
                                  ...params.InputProps,
                                  className: classes.inputText,
                                }}
                              />
                            )}
                          />
                        </FieldGroup>
                      </Grid>

                      <Grid item>
                        <FieldGroup label="Status Name">
                          <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            placeholder="e.g. Completed"
                            sx={INPUT_SX}
                            inputRef={milestoneStatusNameRef}
                            inputProps={{ maxLength: MILESTONE_MAX_LENGTH }}
                            InputProps={{ className: classes.inputText }}
                            onChange={(e) => {
                              milestoneStatusNameRef.current.value =
                                e.target.value.replace(/[:\-]/g, " ");
                            }}
                          />
                        </FieldGroup>
                      </Grid>

                      <Grid item>
                        <Tooltip title="Add status" placement="top">
                          <IconButton
                            onClick={handleAddMilestoneStatus}
                            aria-label="Add Milestone Status"
                            sx={ADD_BUTTON_SX}
                          >
                            <AddCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </AddPanel>

                  {/* Grid or empty state */}
                  {milestoneStatuses.length > 0 ? (
                    <EnhancedDataGrid
                      key="MilestoneStatuses"
                      headCells={statusHeadCells}
                      dataSource={milestoneStatuses}
                      handleSave={handleSaveMilestoneStatusEdit}
                      isReadOnly={false}
                      setVisibleRows={setMilestoneStatuses}
                      dragEnabled={false}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: 5,
                        gap: 1,
                      }}
                    >
                      <InfoOutlined sx={{ fontSize: 28, color: "#CFD8DC" }} />
                      <Typography
                        sx={{
                          color: "#8A9BB0",
                          fontFamily: "Urbanist, sans-serif",
                          fontSize: "14px",
                        }}
                      >
                        {noStatusMessage}
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <SnackBar snack={snack} snackReset={snackReset} />

      <ApplyToAllDialog
        open={applyToAllDialog.open}
        onClose={handleApplyToAllClose}
        milestoneName={applyToAllDialog.milestoneName}
        milestoneId={applyToAllDialog.milestoneId}
        onProceed={handleApplyToAllProceed}
      />
    </div>
  );
}
